import {
  Alert,
  Button,
  Card,
  CardBody,
  Typography,
} from '@statsbomb/kitbag-components';
import { useVideoCurrentTime } from '@statsbomb/video-client-js';
import { useAtomValue } from 'jotai';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import { currentlyPlayingAtom } from '../../atoms/playlist';
import { selectedMetadataAtom } from '../../atoms/selectedMetadata';
import { selectedSourceDataAtom } from '../../atoms/selectedSourceData';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import { sourceSerialiser } from '../../serialisers/sourceData';
import { videoReferenceSerialiser } from '../../serialisers/videoReference';
import themeHelper from '../../utils/themeHelper';
import {
  microsecondsToSeconds,
  secondsToTimeStamp,
  stripTimemarker,
  timestampAsMicroseconds,
} from '../../utils/time';
import CardHeader from '../CardHeader';
import MetadataSelections from '../MetadataSelections';
import {
  DEFAULT_QUALITY_SETTINGS,
  VideoQualitySettings,
} from '../VideoQualitySettings/VideoQualitySettings';

const Container = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: flex-start;
`;
const ButtonContainer = styled.div`
  padding-top: ${themeHelper.spacing()};
  display: flex;
  justify-content: flex-start;
`;

const MISSING_VIDEO_INFO_MESSAGE = (
  <FormattedMessage
    defaultMessage="Some video information is missing. Please check Match ID and Period are populated"
    id="7f102"
  />
);
const MISSING_SOURCE_INFO_MESSAGE = (
  <FormattedMessage defaultMessage="Source information is missing" id="8c278" />
);

const utiliseInternalProcessing = () =>
  !!+process.env.REACT_APP_USE_INTERNAL_PROCESSING_FOR_TRIM;

export function TrimVideo({ onSubmitCompleted }) {
  const api = useApi();
  const intl = useIntl();

  const currentlyPlaying = useAtomValue(currentlyPlayingAtom);
  const currentPlaybackTime = useVideoCurrentTime();
  const sourceData = useAtomValue(selectedSourceDataAtom);
  const selectedMetadata = useAtomValue(selectedMetadataAtom);

  const {
    sport,
    matchId,
    period: matchPart,
    version,
    track,
  } = selectedMetadata;

  const [message, setMessage] = useState();

  const isCurrentlyPlayingAnMp4 = currentlyPlaying.url.includes('.mp4');

  const isCurrentlyPlayingHLSStream = currentlyPlaying.url.includes('.m3u8');

  const [trimFrom, setTrimFrom] = useState('');
  const [trimTo, setTrimTo] = useState('');

  const [qualitySettings, setQualitySettings] = useState({
    bitrate: DEFAULT_QUALITY_SETTINGS.MAX_BITRATE,
    framerate: DEFAULT_QUALITY_SETTINGS.FRAMERATE,
    resolution: DEFAULT_QUALITY_SETTINGS.RESOLUTION,
  });

  const submitTrimRequest = async () => {
    const singleSegment = {
      source: stripTimemarker(currentlyPlaying.url),
    };

    if (trimFrom) singleSegment.from = trimFrom;
    if (trimTo) singleSegment.to = trimTo;

    const data = {
      name: intl.formatMessage(
        {
          defaultMessage: 'ingested-clip-{date}',
          id: 'f986c',
        },
        { date: new Date().toISOString() },
      ),
      quality_settings: qualitySettings,
      ...sourceSerialiser(sourceData),
    };

    if (!matchId || !matchPart) {
      setMessage({
        text: MISSING_VIDEO_INFO_MESSAGE,
        type: 'error',
      });
      return;
    }
    if (!sourceData.sourceName) {
      setMessage({
        text: MISSING_SOURCE_INFO_MESSAGE,
        type: 'error',
      });
      return;
    }
    data.video_reference = videoReferenceSerialiser({
      matchId,
      matchPart,
      sport,
      track,
      version,
    });

    if (utiliseInternalProcessing()) {
      data.url = stripTimemarker(currentlyPlaying.url);
      data.start_timestamp = timestampAsMicroseconds(trimFrom, {
        includesHours: true,
      });
      data.end_timestamp = timestampAsMicroseconds(trimTo, {
        includesHours: true,
      });
      await api.post(`${API_ENDPOINTS.videoService}/downloads`, {
        data,
      });
    } else {
      data.segments = [singleSegment];
      await api.put(`${API_ENDPOINTS.videoService}/videos/ingest`, {
        data: JSON.stringify(data),
      });
    }

    if (api.errorMessage) {
      setMessage({
        text: intl.formatMessage({
          defaultMessage: 'Ingest Clip Failed',
          id: 'ff3d3',
        }),
        type: 'error',
      });
    } else {
      setMessage({
        text: intl.formatMessage({
          defaultMessage: 'Ingest Clip Requested',
          id: '53320',
        }),
      });
      onSubmitCompleted();
    }
  };

  const validateTrimRequestForMp4 = () => {
    setMessage('');
    const timestampMatcher = /^(?:1[0-2]|0[0-9]):[0-5][0-9]:[0-5][0-9]$/;

    if (!trimFrom.match(timestampMatcher) || !trimTo.match(timestampMatcher)) {
      return intl.formatMessage({
        defaultMessage: 'Times should be in the format HH:MM:SS',
        id: '96bfa',
      });
    }

    if (
      !(
        timestampAsMicroseconds(trimTo, { includesHours: true }) >
        timestampAsMicroseconds(trimFrom, { includesHours: true })
      )
    ) {
      return intl.formatMessage({
        defaultMessage: 'End time cannot be before start time',
        id: 'b0a48',
      });
    }

    return '';
  };

  const trimRequested = async () => {
    const validationFailureMessage = isCurrentlyPlayingAnMp4
      ? validateTrimRequestForMp4()
      : '';

    if (validationFailureMessage) {
      setMessage({ text: validationFailureMessage, type: 'warning' });
    } else {
      submitTrimRequest();
    }
  };

  const disabled = !currentlyPlaying?.url;

  const handleUseCurrentAsTrimFrom = () => {
    setTrimFrom(secondsToTimeStamp(microsecondsToSeconds(currentPlaybackTime)));
  };
  const handleUseCurrentAsTrimTo = () => {
    setTrimTo(secondsToTimeStamp(microsecondsToSeconds(currentPlaybackTime)));
  };

  return (
    <div data-testid="clip-selection">
      {currentlyPlaying?.url && (
        <Card>
          <CardHeader>
            <FormattedMessage
              defaultMessage="Clip Request Details"
              id="dde5d"
            />
          </CardHeader>
          <CardBody>
            {!isCurrentlyPlayingAnMp4 && !isCurrentlyPlayingHLSStream && (
              <FormattedMessage
                defaultMessage="Play either an mp4 or a HLS stream"
                id="555da"
              />
            )}
            {(isCurrentlyPlayingAnMp4 || isCurrentlyPlayingHLSStream) && (
              <>
                {api.errorMessage && (
                  <Alert type="error">
                    <Typography variant="headingSmall">
                      {api.errorMessage}
                    </Typography>
                  </Alert>
                )}
                {!api.errorMessage && message && (
                  <Alert type={message.type}>
                    <Typography variant="headingSmall">
                      {message.text}
                    </Typography>
                  </Alert>
                )}
                <Container>
                  <MetadataSelections.Timestamp
                    disabled={disabled}
                    label={intl.formatMessage({
                      defaultMessage: 'Trim From',
                      id: 'beb7b',
                    })}
                    labelPosition="top"
                    setTimestamp={setTrimFrom}
                    timestamp={trimFrom}
                  />
                  <Button
                    onClick={handleUseCurrentAsTrimFrom}
                    size="small"
                    title={intl.formatMessage(
                      { defaultMessage: 'Clip from {time}', id: 'b2ddf' },
                      {
                        time: secondsToTimeStamp(
                          microsecondsToSeconds(currentPlaybackTime),
                        ),
                      },
                    )}
                    variant="secondary"
                  >
                    <FormattedMessage
                      defaultMessage="Use {time}"
                      id="94f09"
                      values={{
                        time: secondsToTimeStamp(
                          microsecondsToSeconds(currentPlaybackTime),
                        ),
                      }}
                    />
                  </Button>
                </Container>
                <Container>
                  <MetadataSelections.Timestamp
                    disabled={disabled}
                    label={intl.formatMessage({
                      defaultMessage: 'Trim To',
                      id: 'f7c4d',
                    })}
                    labelPosition="top"
                    setTimestamp={setTrimTo}
                    timestamp={trimTo}
                  />
                  <Button
                    onClick={handleUseCurrentAsTrimTo}
                    size="small"
                    title={intl.formatMessage(
                      { defaultMessage: 'Clip to {time}', id: '888a6' },
                      {
                        time: secondsToTimeStamp(
                          microsecondsToSeconds(currentPlaybackTime),
                        ),
                      },
                    )}
                    variant="secondary"
                  >
                    <FormattedMessage
                      defaultMessage="Use {time}"
                      id="94f09"
                      values={{
                        time: secondsToTimeStamp(
                          microsecondsToSeconds(currentPlaybackTime),
                        ),
                      }}
                    />
                  </Button>
                </Container>
                <VideoQualitySettings
                  qualitySettings={qualitySettings}
                  setQualitySettings={setQualitySettings}
                />
                <ButtonContainer>
                  <Button onClick={trimRequested} isPending={api.isFetching}>
                    <FormattedMessage defaultMessage="Ingest Clip" id="83119" />
                  </Button>
                </ButtonContainer>
              </>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

TrimVideo.propTypes = {
  onSubmitCompleted: PropTypes.func,
};
TrimVideo.defaultProps = {
  onSubmitCompleted: () => {},
};

export default { TrimVideo };
